import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Input,
  Checkbox,
  Text,
  VStack,
} from "@chakra-ui/react";

const toLocalDateTimeValue = (value) => {
  const date = value ? new Date(value) : new Date(Date.now() + 48 * 60 * 60 * 1000);
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

export function ManageVoiceSimInvitationModal({
  isOpen,
  onClose,
  invitation,
  onSubmit,
  isSubmitting,
}) {
  const [expiresAt, setExpiresAt] = useState("");
  const [remainingAttempts, setRemainingAttempts] = useState(1);
  const [resendEmail, setResendEmail] = useState(false);

  const currentRemainingAttempts = useMemo(() => {
    return Math.max(0, Number(invitation?.attemptsAllowed || 0) - Number(invitation?.attemptsUsed || 0));
  }, [invitation?.attemptsAllowed, invitation?.attemptsUsed]);

  useEffect(() => {
    if (!isOpen || !invitation) return;
    setExpiresAt(toLocalDateTimeValue(invitation.expiresAt));
    setRemainingAttempts(Math.max(1, currentRemainingAttempts || 1));
    setResendEmail(false);
  }, [isOpen, invitation, currentRemainingAttempts]);

  const isValid = Boolean(invitation) && Boolean(expiresAt) && Number(remainingAttempts) >= 1;

  const handleSubmit = async () => {
    if (!isValid || !invitation) return;
    await onSubmit({
      voiceSimSessionId: invitation.id,
      expiresAt: new Date(expiresAt).toISOString(),
      remainingAttempts: Number(remainingAttempts),
      resendEmail,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Manage Voice Sim Invite</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.300">
              {invitation?.scenario?.title || "Voice Simulation"} for {invitation?.studentEmail || "recipient"}
            </Text>

            <FormControl isRequired>
              <FormLabel>Invite Expiration</FormLabel>
              <Input
                type="datetime-local"
                value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)}
                bg="gray.700"
                borderColor="gray.600"
              />
              <FormHelperText color="gray.400">
                Updating this keeps the same invite link active until the new expiration.
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Remaining Attempts</FormLabel>
              <NumberInput
                min={1}
                max={25}
                value={remainingAttempts}
                onChange={(_, valueAsNumber) => setRemainingAttempts(Number.isFinite(valueAsNumber) ? valueAsNumber : 1)}
              >
                <NumberInputField bg="gray.700" borderColor="gray.600" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText color="gray.400">
                Current remaining attempts: {currentRemainingAttempts}. This sets how many attempts should be available from now.
              </FormHelperText>
            </FormControl>

            <FormControl>
              <Checkbox
                isChecked={resendEmail}
                onChange={(event) => setResendEmail(event.target.checked)}
                colorScheme="blue"
              >
                Resend the invite email
              </Checkbox>
              <FormHelperText color="gray.400">
                If checked, the student will receive the existing invite link again with the updated expiration.
              </FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isDisabled={!isValid}
            isLoading={isSubmitting}
            loadingText={resendEmail ? "Updating + Sending" : "Updating"}
          >
            {resendEmail ? "Save + Resend" : "Save Changes"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
